import { cn } from "@newxsystems/shared";
import type { ReactNode } from "react";
import { resolveKaajPrimitive } from "../../../../client";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import type {
  IOfficeCardsBlockProps,
  OfficeCardsBlockActionInput,
  OfficeCardsBlockAddress,
  OfficeCardsBlockLink,
  OfficeCardsBlockOffice,
} from "./OfficeCardsBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

const KaajImage = resolveKaajPrimitive("Image");
const KaajLink = resolveKaajPrimitive("Link");

const isNonEmptyString = (value?: string | null): value is string =>
  typeof value === "string" && value.trim().length > 0;

const isExternalHref = (href: string): boolean => /^https?:\/\//i.test(href);

const getRel = (href: string, target?: string): string | undefined => {
  if (target === "_blank") {
    return "noopener noreferrer";
  }

  return isExternalHref(href) ? "noopener noreferrer" : undefined;
};

const formatAddress = ({
  city,
  country,
  line1,
  line2,
  postalCode,
  region,
}: OfficeCardsBlockAddress = {}): string[] => {
  const locality = [city, [region, postalCode].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");

  return [line1, line2, locality, country].filter(Boolean) as string[];
};

const getLocationLabel = (address?: OfficeCardsBlockAddress | null): string =>
  [address?.city, address?.region].filter(isNonEmptyString).join(", ");

const getEmailHref = (email?: string | null): string | undefined =>
  isNonEmptyString(email) ? `mailto:${email}` : undefined;
const getPhoneHref = (phone?: string | null): string | undefined =>
  isNonEmptyString(phone) ? `tel:${phone.replace(/[^+\d]/g, "")}` : undefined;

type DetailIconName = "address" | "email" | "hours" | "phone";

const DetailIconSvg = ({ name }: { name: DetailIconName }) => {
  if (name === "address") {
    return (
      <svg
        aria-hidden="true"
        className="size-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        viewBox="0 0 24 24"
      >
        <path
          d="M12 21s6-5.2 6-11a6 6 0 0 0-12 0c0 5.8 6 11 6 11Z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="10" r="2" />
      </svg>
    );
  }

  if (name === "hours") {
    return (
      <svg
        aria-hidden="true"
        className="size-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        viewBox="0 0 24 24"
      >
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v4l3 2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (name === "phone") {
    return (
      <svg
        aria-hidden="true"
        className="size-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        viewBox="0 0 24 24"
      >
        <path
          d="M7 5 5.8 6.2c-.8.8-.9 2.1-.2 3.3a22 22 0 0 0 8.9 8.9c1.2.7 2.5.6 3.3-.2L19 17l-3.2-3.2-1.5 1.5a14 14 0 0 1-5.6-5.6l1.5-1.5L7 5Z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg
      aria-hidden="true"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      viewBox="0 0 24 24"
    >
      <path d="M4.5 7.5h15v9h-15z" strokeLinejoin="round" />
      <path d="m5 8 7 5 7-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const DetailIcon = ({ name }: { name: DetailIconName }) => (
  <span
    aria-hidden="true"
    className="mt-0.5 inline-flex size-10 shrink-0 items-center justify-center rounded-2xl border border-[#CAD7FF] bg-[#EEF2FF] text-[#2144FF] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]"
  >
    <DetailIconSvg name={name} />
  </span>
);

const DetailRow = ({
  children,
  icon,
  label,
}: {
  children?: ReactNode;
  icon: DetailIconName;
  label: string;
}) => {
  if (!children) {
    return null;
  }

  return (
    <div className="grid grid-cols-[2.5rem_minmax(0,1fr)] gap-3 border-t border-[#E2E9FF] pt-4 first:border-t-0 first:pt-0">
      <DetailIcon name={icon} />
      <div className="min-w-0">
        <dt className="text-[0.72rem] font-extrabold uppercase tracking-[0.14em] text-[#405076]">
          {label}
        </dt>
        <dd className="mt-1 text-sm leading-6 text-[#151927] [overflow-wrap:anywhere]">
          {children}
        </dd>
      </div>
    </div>
  );
};

const LinkText = ({ children, href }: { children?: string; href?: string }) => {
  if (!isNonEmptyString(children)) {
    return null;
  }

  if (!href) {
    return <span>{children}</span>;
  }

  return (
    <KaajLink
      className="inline-flex min-h-11 max-w-full items-center rounded-full text-[#2144FF] underline decoration-[#BFD0FF] underline-offset-4 transition-colors duration-200 [overflow-wrap:anywhere] hover:text-[#132EDB] hover:decoration-[#132EDB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-2 motion-reduce:transition-none"
      href={href}
    >
      {children}
    </KaajLink>
  );
};

const isPrimaryAction = (
  action: OfficeCardsBlockActionInput | OfficeCardsBlockLink,
): action is OfficeCardsBlockActionInput => "style" in action && action.style === "primary";

const ActionLink = ({
  action,
  compact = false,
}: {
  action: OfficeCardsBlockActionInput | OfficeCardsBlockLink;
  compact?: boolean;
}) => {
  if (!action.href || !action.text) {
    return null;
  }

  const primary = isPrimaryAction(action);

  return (
    <KaajLink
      aria-label={action.ariaLabel || action.text}
      className={cn(
        "inline-flex min-h-11 items-center justify-center rounded-full border px-5 py-2.5 text-center text-sm font-extrabold transition-[background-color,border-color,box-shadow,color,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-4 motion-reduce:transition-none motion-safe:hover:-translate-y-0.5 max-[420px]:w-full",
        primary
          ? "border-[#2144FF] bg-[#2144FF] text-white shadow-[0_14px_32px_rgba(33,68,255,0.24)] hover:border-[#132EDB] hover:bg-[#132EDB]"
          : "border-[#BFD0FF] bg-white text-[#2144FF] shadow-[0_12px_30px_rgba(33,68,255,0.08)] hover:border-[#2144FF] hover:bg-[#F6F1FF] hover:text-[#132EDB]",
        compact && "w-full sm:w-auto",
      )}
      href={action.href}
      rel={getRel(action.href, action.target ?? undefined)}
      target={action.target ?? undefined}
    >
      {action.text}
      <span aria-hidden="true" className="ml-2">
        →
      </span>
    </KaajLink>
  );
};

const ImageFrame = ({ office }: { office: OfficeCardsBlockOffice }) => {
  const image = office.image;

  if (!image) {
    return null;
  }

  return (
    <figure className="relative h-full w-full overflow-hidden rounded-[1.55rem] border border-[#CAD7FF] bg-[#EEF2FF] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.92)]">
      <div className="relative h-full min-h-[16rem] overflow-hidden rounded-[1.15rem] border border-white/70 bg-[#D9E1FF] sm:min-h-[18rem] lg:min-h-full">
        <KaajImage
          alt={image.alternateText}
          className="aspect-[16/10] h-full w-full object-cover transition-transform duration-500 motion-reduce:transition-none motion-safe:group-hover/card:scale-[1.018] lg:aspect-auto"
          decoding="async"
          height={image.height ?? undefined}
          loading="lazy"
          sizes={image.sizes ?? "(min-width: 1280px) 32rem, (min-width: 768px) 42vw, 92vw"}
          src={image.defaultSrc}
          srcSet={image.srcSet ?? undefined}
          style={
            image.focalPoint
              ? { objectPosition: `${image.focalPoint.x}% ${image.focalPoint.y}%` }
              : undefined
          }
          width={image.width ?? undefined}
        />
      </div>
      {isNonEmptyString(image.caption) ? (
        <figcaption className="absolute inset-x-5 bottom-5 rounded-2xl border border-white/75 bg-white/94 px-3 py-2 text-xs font-bold leading-5 text-[#405076] shadow-[0_10px_24px_rgba(21,25,39,0.14)]">
          {image.caption}
        </figcaption>
      ) : null}
    </figure>
  );
};

const MapFrame = ({ office }: { office: OfficeCardsBlockOffice }) =>
  office.map?.src && office.map.title ? (
    <div className="relative h-full w-full overflow-hidden rounded-[1.55rem] border border-[#CAD7FF] bg-[#EEF2FF] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.92)]">
      <div className="relative h-full min-h-[16rem] overflow-hidden rounded-[1.15rem] border border-white/70 bg-white sm:min-h-[18rem] lg:min-h-full">
        <iframe
          className="absolute inset-0 h-full w-full border-0 bg-white saturate-[1.05] contrast-[1.04]"
          loading="eager"
          referrerPolicy="no-referrer-when-downgrade"
          src={office.map.src}
          title={office.map.title}
        />
      </div>
    </div>
  ) : null;

const MediaFrame = ({ office }: { office: OfficeCardsBlockOffice }) =>
  office.map?.src && office.map.title ? (
    <MapFrame office={office} />
  ) : (
    <ImageFrame office={office} />
  );

const OfficeCard = ({ office }: { office: OfficeCardsBlockOffice }) => (
  <article className="group/card relative isolate flex h-full min-w-0 flex-col overflow-hidden rounded-[2rem] border border-[#D9E1FF] bg-white text-[#151927] shadow-[0_24px_70px_rgba(33,68,255,0.1)] transition-[border-color,box-shadow,transform] duration-300 motion-reduce:transition-none motion-safe:hover:-translate-y-1 motion-safe:hover:border-[#BFD0FF] motion-safe:hover:shadow-[0_32px_86px_rgba(33,68,255,0.15)] lg:grid lg:grid-cols-[minmax(0,0.9fr)_minmax(20rem,0.72fr)] lg:items-stretch">
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#B8C7FF] to-transparent"
    />
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -right-16 top-20 -z-10 size-40 rounded-full bg-[#D9E1FF]/54 blur-3xl transition-transform duration-500 motion-reduce:transition-none motion-safe:group-hover/card:scale-110"
    />

    <div className="flex flex-1 flex-col gap-6 p-5 sm:p-7 lg:p-8">
      <div className="space-y-3">
        {getLocationLabel(office.address) ? (
          <p className="inline-flex max-w-full items-center rounded-full border border-[#CAD7FF] bg-[#EEF2FF] px-3 py-1 text-xs font-extrabold text-[#2144FF] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] [overflow-wrap:anywhere]">
            {getLocationLabel(office.address)}
          </p>
        ) : null}
        <h3 className="text-balance text-2xl font-extrabold leading-tight tracking-[-0.035em] text-[#151927] [overflow-wrap:anywhere] md:text-3xl">
          {office.name}
        </h3>
      </div>

      {office.description ? (
        <p className="text-pretty text-sm leading-6 text-[#4B5568] [overflow-wrap:anywhere]">
          {office.description}
        </p>
      ) : null}

      <dl className="grid gap-4">
        {formatAddress(office.address ?? undefined).length > 0 ? (
          <DetailRow icon="address" label="Address">
            <address className="not-italic">
              {formatAddress(office.address ?? undefined).map((line, lineIndex) => (
                <span className="block" key={`${line}-${lineIndex}`}>
                  {line}
                </span>
              ))}
            </address>
          </DetailRow>
        ) : null}
        {office.hours ? (
          <DetailRow icon="hours" label="Hours">
            {office.hours}
          </DetailRow>
        ) : null}
        {office.phone ? (
          <DetailRow icon="phone" label="Phone">
            <LinkText href={getPhoneHref(office.phone)}>{office.phone}</LinkText>
          </DetailRow>
        ) : null}
        {office.email ? (
          <DetailRow icon="email" label="Email">
            <LinkText href={getEmailHref(office.email)}>{office.email}</LinkText>
          </DetailRow>
        ) : null}
      </dl>

      {office.directionsLink?.href && office.directionsLink.text ? (
        <div className="mt-auto pt-1">
          <ActionLink action={office.directionsLink} compact />
        </div>
      ) : null}
    </div>

    <div className="border-t border-[#D9E1FF] bg-[linear-gradient(180deg,#FAFBFF_0%,#F1F4FF_100%)] p-3 sm:p-4 lg:flex lg:h-full lg:min-h-full lg:border-l lg:border-t-0">
      <MediaFrame office={office} />
    </div>
  </article>
);

export const OfficeCardsBlock = (props: IOfficeCardsBlockProps) => {
  const { buttons, anchorId, parentClass, spacing } = props;
  const { eyebrow, heading, subheading } = props;
  const actions = buttons ?? [];
  const offices = props.offices ?? [];
  const sectionId = anchorId ?? undefined;
  const headingId = heading && sectionId ? `${sectionId}-heading` : undefined;

  if (offices.length === 0 && !eyebrow && !heading && !subheading && actions.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "relative isolate overflow-hidden text-[#151927]",
        getKaajSectionSpacing(spacing),
        parentClass,
      )}
      data-block-handle="officeCards"
      id={sectionId}
    >
      <div className="kaaj-container relative space-y-10 lg:space-y-14">
        {(eyebrow || heading || subheading || actions.length > 0) && (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,0.76fr)_auto] lg:items-end">
            <div className="max-w-3xl space-y-4">
              {eyebrow ? (
                <p className="inline-flex max-w-full items-center gap-2 rounded-full border border-[#CAD7FF] bg-white/92 px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.18em] text-[#2144FF] shadow-[0_10px_26px_rgba(33,68,255,0.1)] [overflow-wrap:anywhere]">
                  <span className="size-2 rounded-full bg-[#55D6C2]" />
                  {eyebrow}
                </p>
              ) : null}
              {heading ? (
                <h2 className="kaaj-section-heading max-w-3xl text-[#151927]" id={headingId}>
                  {heading}
                </h2>
              ) : null}
              {subheading ? (
                <RichTextHtml
                  className="max-w-[64ch] text-pretty text-base leading-7 text-[#4B5568] sm:text-lg sm:leading-8"
                  html={subheading}
                />
              ) : null}
            </div>
            {actions.length > 0 && (
              <div className="flex flex-wrap gap-3 lg:justify-end">
                {actions.map((action, index) => (
                  <ActionLink action={action} key={`${action.href}-${index}`} />
                ))}
              </div>
            )}
          </div>
        )}

        {offices.length > 0 ? (
          <div className="grid gap-5 lg:gap-6">
            {offices.map((office, index) => (
              <div key={`${office.name}-${index}`}>
                <OfficeCard office={office} />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

OfficeCardsBlock.displayName = "Block::OfficeCardsBlock";
